import Fuse from 'fuse.js';
import type { ResourcePickerItem } from '../types';

type SearchableItem = {
  label: string;
  id: string;
  idLabel: string;
  provider?: string;
  providerLabel?: string;
  typeLabel?: string;
  searchKeywords?: string[];
  available?: boolean;
};

const FUSE_KEYS = [
  { name: 'label', weight: 0.5 },
  { name: 'idLabel', weight: 0.22 },
  { name: 'id', weight: 0.12 },
  { name: 'providerLabel', weight: 0.08 },
  { name: 'provider', weight: 0.04 },
  { name: 'searchKeywords', weight: 0.03 },
  { name: 'typeLabel', weight: 0.01 },
];

const FUSE_THRESHOLD = 0.35;

const normalizeSearchText = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const getTokens = (value: string): string[] =>
  normalizeSearchText(value)
    .split(' ')
    .filter((token) => token.length > 0);

const toSearchLabel = (value: string): string => getTokens(value).join(' ');

const containsTokensInOrder = (
  queryTokens: string[],
  candidateTokens: string[]
): boolean => {
  if (queryTokens.length === 0) {
    return false;
  }

  let candidateIndex = 0;

  for (const queryToken of queryTokens) {
    while (
      candidateIndex < candidateTokens.length &&
      !candidateTokens[candidateIndex]?.startsWith(queryToken)
    ) {
      candidateIndex += 1;
    }

    if (candidateIndex === candidateTokens.length) {
      return false;
    }

    candidateIndex += 1;
  }

  return true;
};

const getRankingBoost = (query: string, item: SearchableItem): number => {
  const queryText = normalizeSearchText(query);
  const queryTokens = getTokens(query);
  const labelText = normalizeSearchText(item.label);
  const labelTokens = getTokens(item.label);
  const searchableText = normalizeSearchText(
    [
      item.label,
      item.id,
      item.idLabel,
      item.provider,
      item.providerLabel,
      item.typeLabel,
      ...(item.searchKeywords ?? []),
    ]
      .filter((value): value is string => value !== undefined)
      .join(' ')
  );

  let boost = 0;

  if (labelText === queryText) {
    boost += 100;
  }

  if (labelText.includes(queryText)) {
    boost += 50;
  }

  if (containsTokensInOrder(queryTokens, labelTokens)) {
    boost += 40;
  }

  if (queryTokens.every((token) => searchableText.includes(token))) {
    boost += 20;
  }

  if (item.available === true) {
    boost += 10;
  }

  return boost;
};

/**
 * Create a reusable fuzzy filter for resource picker items. Returns a function
 * that accepts a query string and returns matched items in relevance order.
 * When the query is empty, returns all items unchanged.
 */
export const createResourcePickerFilter = <T>(
  items: ResourcePickerItem<T>[]
): ((query: string) => ResourcePickerItem<T>[]) => {
  const searchable: SearchableItem[] = items.map((item) => ({
    label: item.label,
    id: item.id,
    idLabel: toSearchLabel(item.id),
    provider: item.provider,
    providerLabel: item.providerLabel,
    typeLabel: item.typeLabel,
    searchKeywords: item.searchKeywords,
    available: item.available,
  }));

  const fuse = new Fuse(searchable, {
    keys: FUSE_KEYS,
    threshold: FUSE_THRESHOLD,
    ignoreLocation: true,
    includeScore: true,
  });

  // Build an id→item lookup for O(1) result mapping.
  const byId = new Map<string, ResourcePickerItem<T>>();
  for (const item of items) {
    byId.set(item.id, item);
  }

  return (query: string): ResourcePickerItem<T>[] => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      return items;
    }
    return fuse
      .search(trimmed)
      .sort((a, b) => {
        const aBoost = getRankingBoost(trimmed, a.item);
        const bBoost = getRankingBoost(trimmed, b.item);

        if (aBoost !== bBoost) {
          return bBoost - aBoost;
        }

        return (a.score ?? 0) - (b.score ?? 0);
      })
      .map((result) => byId.get(result.item.id))
      .filter((item): item is ResourcePickerItem<T> => item !== undefined);
  };
};
